import React, { useEffect, useState } from "react";
import { PageContainer, PageHeader, Card, ConfirmDelete } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Plus, PencilSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import ImageUpload from "./ImageUpload";
import { runValidators, validators } from "./validators";
import BrandDetailEditor from "./BrandDetailEditor";

const empty = { name: "", slug: "", tagline: "", description: "", external_url: "", logo_id: null, image_id: null, order: 0, status: "published", detail: null };

export default function BrandsManager() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = async () => setItems((await adminApi.get("/admin/brands")).data);
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (b) => {
    setEditing(b);
    setForm({ ...empty, ...b, detail: b.detail || null });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Brand name required");
    const urlErr = runValidators(form.external_url || "", [validators.url]);
    if (urlErr) return toast.error(`External URL: ${urlErr}`);
    if (form.slug && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(form.slug)) return toast.error("Slug: lowercase letters, digits and dashes only");

    setBusy(true);
    try {
      if (editing) await adminApi.put(`/admin/brands/${editing.id}`, form);
      else await adminApi.post("/admin/brands", form);
      toast.success(editing ? "Updated" : "Brand added");
      setOpen(false);
      await load();
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    await adminApi.delete(`/admin/brands/${id}`);
    await load();
    toast.success("Deleted");
  };

  return (
    <PageContainer>
      <PageHeader
        title="Product Brands"
        subtitle="Brands featured on the public Products section. Each card links out to the brand's official site."
        action={<Button onClick={openCreate} className="text-white" style={{ background: "#1B6FE0" }} data-testid="brand-new"><Plus size={14} weight="bold" /> Add brand</Button>}
      />

      <Card>
        <ul className="divide-y divide-slate-100" data-testid="brand-list">
          {items.length === 0 && <li className="p-8 text-center text-[#6B7A8D]">No brands yet.</li>}
          {items.map((b) => (
            <li key={b.id} className="p-4 flex items-center gap-3" data-testid={`brand-item-${b.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[14.5px]" style={{ color: "#0F2A6E" }}>{b.name}</div>
                <div className="text-[12px] text-[#6B7A8D] truncate">{b.tagline || "—"} · order {b.order} · <a href={b.external_url} target="_blank" rel="noopener noreferrer" className="text-[#1B6FE0] hover:underline">{b.external_url || "no link"}</a></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(b)} data-testid={`brand-edit-${b.id}`}><PencilSimple size={13} weight="bold" /></Button>
              <ConfirmDelete title="Delete this brand?" description={`"${b.name}" will be removed from the public Products section.`} onConfirm={() => del(b.id)} testId={`brand-del-${b.id}`}>
                <Button variant="outline" size="sm" className="text-[#1B6FE0] hover:bg-blue-50 hover:text-[#1B6FE0]" data-testid={`brand-del-${b.id}`}><Trash size={13} weight="bold" /></Button>
              </ConfirmDelete>
            </li>
          ))}
        </ul>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="brand-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit brand" : "Add brand"}</DialogTitle>
            <DialogDescription>Brands are shown on the public Products section in ascending order.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Name</Label>
                <Input className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} required data-testid="brand-name" />
              </div>
              <div>
                <Label>Tagline</Label>
                <Input className="mt-1.5" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} data-testid="brand-tagline" />
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Slug (URL path · auto from name if empty)</Label>
                <Input className="mt-1.5 font-mono text-[13px]" placeholder="auto" value={form.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/\s+/g,"-"))} data-testid="brand-slug" />
              </div>
              <div>
                <Label>External URL</Label>
                <Input className="mt-1.5" placeholder="https://ritchfield.com/" value={form.external_url} onChange={(e) => set("external_url", e.target.value)} data-testid="brand-url" />
              </div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Textarea rows={3} className="mt-1.5" value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <ImageUpload value={form.logo_id} onChange={(id) => set("logo_id", id)} label="Brand logo (square recommended)" testId="brand-logo" />
            <ImageUpload value={form.image_id} onChange={(id) => set("image_id", id)} label="Brand hero image (optional)" testId="brand-image" />
            <div className="flex items-center gap-3">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft (hidden)</SelectItem>
                </SelectContent>
              </Select>
              <Label className="ml-3">Order</Label>
              <Input type="number" className="w-24" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0", 10))} />
            </div>
            <div>
              <BrandDetailEditor value={form.detail} onChange={(v) => set("detail", v)} />
              <p className="mt-1 text-[11.5px] text-slate-500">
                Leave everything blank to show a minimal brand landing. Once populated, the page at <code className="bg-slate-100 px-1 rounded">/products/{form.slug || "your-slug"}</code> will render the full detail layout (hero · overview · categories · benefits · applications · support · FAQs · final CTA).
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="brand-save">
                {busy ? "Saving…" : (editing ? "Update brand" : "Add brand")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
